import type { SagaReturnType } from 'redux-saga/effects';
import { put, select, takeLatest } from 'redux-saga/effects';

import { api } from 'src/api';
import { getHotelId } from 'src/routes/Hotel/store/hotel';
import {
  getReviewsPage,
  getReviewsVisible,
  getSorting,
  getStatusReviews,
  getFiltersSelected,
} from './selectors';
import {
  showMoreReviews,
  setVisibleCount,
  setReviewsData,
  setSorting,
  clearReviews,
  setVariant,
  changeFilterStatus,
  setStatus,
  setData,
  retryDownloadAction,
} from './slice';
import { getSortingType } from './utils';

function* initialDownloadReviews(action: ReturnType<typeof setVariant>) {
  const value = action.payload;
  if (value === 'new') {
    yield getNewHotelReviews();
  } else {
    yield getOldHotelReviews();
  }
}

/**
 * Загрузить старые отзывы отеля
 */
function* getOldHotelReviews() {
  try {
    const hotelId: SagaReturnType<typeof getHotelId> = yield select(getHotelId);
    const response: SagaReturnType<typeof api.getReferencesHotelReviews> =
      yield api.getReferencesHotelReviews(Number(hotelId));
    if (response.success && response.reviews) {
      yield put(setReviewsData(response.reviews));
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
}

/**
 * Загрузить новые отзывы отеля
 */
function* getNewHotelReviews() {
  try {
    const hotelId: SagaReturnType<typeof getHotelId> = yield select(getHotelId);
    const currentPage: SagaReturnType<typeof getReviewsPage> = yield select(
      getReviewsPage,
    );
    const status: SagaReturnType<typeof getStatusReviews> = yield select(
      getStatusReviews,
    );
    const sortType = getSortingType(yield select(getSorting));
    const selectedFilters = yield select(getFiltersSelected);
    const params = {
      perPage: 10,
      page: status === 'loading' ? currentPage + 1 : 1,
      sort: sortType,
      filters: selectedFilters,
      marked: selectedFilters !== '',
    };
    const response: SagaReturnType<typeof api.getHotelReview> =
      yield api.getHotelReview(Number(hotelId), params);
    const { reviews, filters, success, pagination } = response;
    if (success && reviews) {
      yield put(setData({ reviews, filters, pagination }));
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    yield put(setStatus('error'));
  }
}

function* downloadMoreReviews() {
  const visibleCount = yield select(getReviewsVisible);
  yield put(setVisibleCount(visibleCount + 10));
  yield put(setStatus('loading'));
  yield getNewHotelReviews();
}

function* updateReviews() {
  yield put(setStatus('upgrading'));
  // очистить все отзывы и пагинацию
  yield put(clearReviews());
  yield getNewHotelReviews();
}

function* retryDownload() {
  yield put(setStatus('loading'));
  yield getNewHotelReviews();
}

export default function* reviewSaga() {
  yield takeLatest(setVariant, initialDownloadReviews);
  yield takeLatest(showMoreReviews, downloadMoreReviews);
  yield takeLatest([changeFilterStatus.type, setSorting.type], updateReviews);
  yield takeLatest(retryDownloadAction, retryDownload);
}
